import React from "react";
import { useTranslation } from "react-i18next";

const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_COMPLEXITY = {
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    digit: /\d/,
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/
};

/**
 * Prüft ein Passwort gegen alle Anforderungen.
 * Kann sowohl von der Komponente als auch extern (Validierung) genutzt werden.
 */
export const checkPasswordRequirements = (password) => {
    const pw = password || "";
    return {
        minLength: pw.length >= PASSWORD_MIN_LENGTH,
        hasLowercase: PASSWORD_COMPLEXITY.lowercase.test(pw),
        hasUppercase: PASSWORD_COMPLEXITY.uppercase.test(pw),
        hasDigit: PASSWORD_COMPLEXITY.digit.test(pw),
        hasSpecial: PASSWORD_COMPLEXITY.special.test(pw)
    };
};

/**
 * Prüft ob alle Passwort-Anforderungen erfüllt sind.
 */
export const isPasswordValid = (password) => {
    const checks = checkPasswordRequirements(password);
    return Object.values(checks).every(Boolean);
};

/**
 * Gibt eine spezifische Fehlermeldung zurück, falls das Passwort ungültig ist.
 */
export const getPasswordError = (password) => {
    if (!password) return "Passwort ist erforderlich.";
    const checks = checkPasswordRequirements(password);
    if (!checks.minLength) return `Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`;
    if (!checks.hasLowercase || !checks.hasUppercase) return "Passwort muss Groß- und Kleinbuchstaben enthalten.";
    if (!checks.hasDigit) return "Passwort muss mindestens eine Zahl enthalten.";
    if (!checks.hasSpecial) return "Passwort muss mindestens ein Sonderzeichen enthalten.";
    return null;
};

/**
 * Echtzeit-Passwort-Anforderungen-Checkliste.
 * Zeigt den Status jeder Anforderung mit ✅/❌ Icons an.
 * Wird nur sichtbar wenn das Passwort-Feld Inhalt hat.
 */
const PasswordRequirements = ({ password }) => {
    const { t } = useTranslation();

    if (!password || password.length === 0) return null;

    const checks = checkPasswordRequirements(password);

    const requirements = [
        {
            key: "minLength",
            met: checks.minLength,
            label: t("admin.users.password.minLength", `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`)
        },
        {
            key: "case",
            met: checks.hasLowercase && checks.hasUppercase,
            label: t("admin.users.password.case", "Groß- und Kleinbuchstaben")
        },
        {
            key: "digit",
            met: checks.hasDigit,
            label: t("admin.users.password.digit", "Mindestens eine Zahl")
        },
        {
            key: "special",
            met: checks.hasSpecial,
            label: t("admin.users.password.special", "Mindestens ein Sonderzeichen (!@#$...)")
        }
    ];

    const allMet = requirements.every((r) => r.met);

    return (
        <div className={`mt-2 p-3 rounded-lg border transition-colors duration-200 ${allMet
                ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                : "bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600"
            }`}>
            <div className="space-y-1.5">
                {requirements.map((req) => (
                    <div
                        key={req.key}
                        className="flex items-center gap-2 transition-all duration-200"
                    >
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${req.met
                                ? "bg-emerald-500 dark:bg-emerald-400"
                                : "bg-slate-300 dark:bg-slate-500"
                            }`}>
                            <i className={`fas text-[8px] text-white transition-transform duration-200 ${req.met ? "fa-check scale-100" : "fa-times scale-90"
                                }`} />
                        </div>
                        <span className={`text-xs transition-colors duration-200 ${req.met
                                ? "text-emerald-700 dark:text-emerald-300"
                                : "text-slate-500 dark:text-slate-400"
                            }`}>
                            {req.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PasswordRequirements;
